/**
 * Instructor assessment tab — UX/UI Specification §9.10.
 *
 * The form itself lives in components/AssessmentForm: the report wizard offers
 * the same one, so an instructor who spots a wrong score while building a
 * report can fix it without leaving the wizard.
 */

import { useState } from 'react';
import {
  ASSESSMENT_KEYS,
  ASSESSMENT_LABELS,
  formatDateLong,
  type AssessmentKey,
  type AssessmentRecord,
} from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { AssessmentForm } from '../components/AssessmentForm.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { useApiData } from '../hooks/useApiData.js';
import { useSession } from '../session/SessionContext.js';
import './AssessmentTab.css';

type Scores = Partial<Record<AssessmentKey, number | null>>;

interface Assessment {
  id: string;
  assessedAt: string;
  scores: Scores;
  previousScores: Scores | null;
  previousAssessedAt: string | null;
  generalNote: string | null;
  nextPeriodFocus: string | null;
}

/** The list shape and the form shape are different views of one row. */
function toRecord(item: Assessment): AssessmentRecord {
  return {
    id: item.id,
    assessedAt: item.assessedAt,
    coreControl: item.scores.coreControl ?? null,
    balance: item.scores.balance ?? null,
    flexibility: item.scores.flexibility ?? null,
    lowerBodyStrength: item.scores.lowerBodyStrength ?? null,
    upperBodyControl: item.scores.upperBodyControl ?? null,
    generalNote: item.generalNote,
    nextPeriodFocus: item.nextPeriodFocus,
  };
}

export function AssessmentTab({ studentId }: { studentId: string }) {
  const { data, error, loading, reload } = useApiData<{ items: Assessment[] }>(
    `/api/students/${studentId}/assessments`,
  );
  const { hasAdminAccess } = useSession();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [listError, setListError] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState<Assessment | null>(null);
  const [reauthFor, setReauthFor] = useState<(() => void) | null>(null);

  const items = data?.items ?? [];

  /** Not reversible, so it asks first. */
  async function remove(item: Assessment) {
    try {
      await api.delete(`/api/students/${studentId}/assessments/${item.id}`);
      reload();
    } catch (err) {
      setListError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  /** Runs now if the admin window is open, otherwise buys ten minutes first. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  return (
    <div className="assess">
      <div className="assess__head">
        <h2 className="profile__section">Eğitmen Değerlendirmesi</h2>
        {!adding && (
          <Button type="button" onClick={() => setAdding(true)}>
            Değerlendirme Ekle
          </Button>
        )}
      </div>

      {/* Constitution §10: these are Pilates observations, not a medical
          result, and the screen says so rather than leaving it implied. */}
      <p className="assess__disclaimer">
        Bu bölüm eğitmenin ders sırasındaki gözlemlerini içerir. Tıbbi bir değerlendirme
        veya teşhis değildir.
      </p>

      {(error || listError) && <p role="alert">{error ?? listError}</p>}

      {adding && (
        <AssessmentForm
          studentId={studentId}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            reload();
          }}
        />
      )}

      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && !adding && (
        <div className="profile__empty">
          <p>Henüz eğitmen değerlendirmesi yapılmamış.</p>
          <Button type="button" onClick={() => setAdding(true)}>
            Değerlendirme Ekle
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <ul className="assess__list">
          {items.map((item) => {
            const rows = ASSESSMENT_KEYS.filter((key) => item.scores[key] != null);
            return (
              <li key={item.id} className="assess__card">
                <div className="assess__card-head">
                  <span className="assess__card-date">
                    {formatDateLong(item.assessedAt)}
                  </span>
                  <span className="assess__card-actions">
                    {editing !== item.id && (
                      <button
                        type="button"
                        className="assess__edit"
                        onClick={() => setEditing(item.id)}
                      >
                        Düzenle
                      </button>
                    )}
                    <button
                      type="button"
                      className="assess__delete"
                      onClick={() => setConfirmDelete(item)}
                    >
                      Sil
                    </button>
                  </span>
                </div>

                {editing === item.id ? (
                  <AssessmentForm
                    studentId={studentId}
                    initial={toRecord(item)}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                      setEditing(null);
                      reload();
                    }}
                  />
                ) : (
                  <>
                    {rows.length > 0 && (
                      <dl className="assess__result">
                        {rows.map((key) => {
                          const current = item.scores[key] as number;
                          const previous = item.previousScores?.[key] ?? null;
                          return (
                            <div key={key} className="assess__result-row">
                              <dt>{ASSESSMENT_LABELS[key]}</dt>
                              <dd>
                                {previous != null && (
                                  <>
                                    <span className="assess__prev">{previous}/5</span>
                                    <span aria-hidden="true"> → </span>
                                  </>
                                )}
                                <span className="assess__now">{current}/5</span>
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    )}

                    {item.generalNote && (
                      <p className="assess__note">
                        <em>Genel Değerlendirme</em> {item.generalNote}
                      </p>
                    )}
                    {item.nextPeriodFocus && (
                      <p className="assess__note">
                        <em>Bir Sonraki Dönem Odağı</em> {item.nextPeriodFocus}
                      </p>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Değerlendirme silinsin mi?"
          body={`${formatDateLong(confirmDelete.assessedAt)} tarihli değerlendirme kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
          confirmLabel="Sil"
          destructive
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const item = confirmDelete;
            setConfirmDelete(null);
            withAdmin(() => void remove(item));
          }}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Değerlendirmeyi kalıcı olarak silmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </div>
  );
}
